import {
  type CellData,
  type ColumnDef,
  type RowData,
  type TableFeatures,
  type TableOptions_All,
  type TableOptions_Plugins,
} from '@tanstack/react-table';
import { useMemo } from 'react';

import type {
  DataTableColumnVisibilityProps,
  DataTableColumnVisibilityState,
} from './column-visibility-types.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { useDataTableConfigElement } from '../../../hooks/useDataTableConfig.js';
import { isGroupColumnDef } from '../../hooks/useTable/datatable-typeguards.js';
import type { DataTableRowData } from '../../row-data-types.js';
import type { TDataTableFeatures } from '../types/features.types.js';

/**
 * Helper function that generates visibility state from original columns.
 * @internal
 */
function getColumnVisibilityFromOriginalColumns<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  columnDefs: readonly ColumnDef<TFeatures, TData, TValue>[],
): DataTableColumnVisibilityState {
  const visibilityState: DataTableColumnVisibilityState = {};

  for (const columnDef of columnDefs) {
    if (isGroupColumnDef<TFeatures, TData>(columnDef) && columnDef.columns) {
      const subColumnDefs = columnDef.columns;
      for (const subColumnDef of subColumnDefs) {
        if (subColumnDef.id) {
          visibilityState[subColumnDef.id] = true;
        }
      }
    }
    // In our scenarios, we enforce id's on everything, but based on the tanstack types,
    // they could still be undefined, hence the check here.
    else if (columnDef.id) {
      visibilityState[columnDef.id] = true;
    }
  }

  return visibilityState;
}

/**
 * Configuration hook for the DataTable ColumnVisibility feature.
 * @internal
 */
export function useColumnVisibility<TData extends DataTableRowData>(
  props: DataTableColumnVisibilityProps,
  // Needs to use TableOptions_all here to work around the UnionToIntersection type not working correctly with plugins.
  // Needs to join the TableOptions_Plugins because right now, they are not part of the TableOptions_all
  options: TableOptions_All<TDataTableFeatures, Exclude<TData, unknown>> &
    TableOptions_Plugins<TDataTableFeatures, Exclude<TData, unknown>>,
): void {
  const columnVisibilityConfig = useDataTableConfigElement('columnVisibility');

  const [columnVisibility, onColumnVisibilityChange] = useControllableState({
    value: props.columnVisibility ?? columnVisibilityConfig,
    defaultValue: props.defaultColumnVisibility,
    onChange: props.onColumnVisibilityChange,
  });

  // eslint-disable-next-line react-hooks/immutability
  options.state = {
    ...options.state,
    columnVisibility,
  };

  // eslint-disable-next-line react-hooks/immutability
  options.onColumnVisibilityChange = onColumnVisibilityChange;
  // eslint-disable-next-line react-hooks/immutability
  options.originalColumnVisibility = useMemo(
    () =>
      props.defaultColumnVisibility ??
      getColumnVisibilityFromOriginalColumns(options.columns),
    [props.defaultColumnVisibility, options.columns],
  );
}
